/**
 * GitlabConnectModal — self-serve GitLab setup to PARITY with GitHub.
 *
 * Shared between:
 *   - IntegrationsPage (the canonical Settings → Integrations page)
 *   - MarketplaceDetailModal (Connect button on a marketplace card whose
 *     workflow declares `gitlab` as a required integration).
 *
 * MULTI-INSTANCE: an account may hold MORE THAN ONE gitlab integration (e.g. a
 * self-hosted instance AND gitlab.com). The backend matches by instanceUrl — a
 * NEW instanceUrl ADDS a row, the SAME instanceUrl updates it. So this modal:
 *   - lists every connected instance (GET /gitlab/integrations — no tokens), and
 *   - keeps the connect form available as an "Add another instance" action that
 *     ADDS (never replaces) by POSTing /gitlab/connect with a different URL.
 *
 * Sections (the modal grows as the user progresses), all so a tenant can wire
 * GitLab END-TO-END without anyone hand-writing DynamoDB rows / tokens:
 *
 *   1. CONNECT — instanceUrl + Personal Access Token, PLUS an optional
 *      `signingToken` (the GitLab-generated `whsec_…` per-webhook secret). The
 *      backend (handlers/gitlab.js connectGitlab) already accepts + stores it
 *      encrypted; the inbound webhook (gitlab-webhook.js) HMAC-validates each
 *      delivery against it. Optional — omit and the legacy static-token path
 *      still works.
 *
 *   2. WEBHOOK SETUP — surfaces the webhook URL + the secret the user must
 *      paste into GitLab (Project/Group → Settings → Webhooks), with the exact
 *      events to enable (Merge request + Comments). Mirrors how a GitHub App
 *      install surfaces its webhook config — here the user configures it by
 *      hand in their GitLab, so we SHOW the values + instructions.
 *
 *   3. REPO/GROUP → AGENT PICKER — the GitLab analog of the GitHub repo→agent
 *      link. The user picks a Zibby project, one of that project's deployed
 *      agents (workflows), and a GitLab project/group, then creates the link
 *      (POST /gitlab/links). That row is EXACTLY what gitlab-webhook.js
 *      resolveLink reads to route an inbound MR to the right review agent.
 *
 * The connect call + links CRUD live in hooks (useConnectGitlab /
 * useGitlabLinks / useCreateGitlabLink / useDeleteGitlabLink) so this component
 * stays presentation-focused. Reset transient state on isOpen transitions so
 * reopening always starts clean.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { IonModal, IonButton, IonSpinner } from '@ionic/react';
import {
  useConnectGitlab,
  useGitlabIntegrations,
  useGitlabLinks,
  useCreateGitlabLink,
  useDeleteGitlabLink,
  useDisconnectIntegration,
} from '../../hooks/useIntegrations';
import api from '../../services/api';
// Reuses the existing .gitlab-*-modal CSS shipped with IntegrationsPage —
// importing here so the styles travel with the component wherever it's
// rendered, instead of relying on the host page having loaded the sheet.
import '../../pages/IntegrationsPage/IntegrationsPage.css';

// Derive the inbound webhook URL the user must configure in GitLab. The
// Webhooks RestApi has a prod custom domain (webhooks-api-prod.zibby.app);
// non-prod points at the API base. We key off REACT_APP_API_BASE_URL (the build
// stamps `https://api-prod.zibby.app` for prod) so the displayed URL matches
// the deployed webhook route.
function webhookUrl() {
  const apiBase = process.env.REACT_APP_API_BASE_URL || '';
  const isProd = /api-prod\.zibby\.app/.test(apiBase) || process.env.NODE_ENV === 'production';
  const stage = isProd ? 'prod' : 'dev';
  return `https://webhooks-api-${stage}.zibby.app/gitlab/webhook`;
}

export default function GitlabConnectModal({ isOpen, onClose, onConnected }) {
  // ── Connect form state ──────────────────────────────────────────────────
  const [instanceUrl, setInstanceUrl] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [signingToken, setSigningToken] = useState('');
  const [error, setError] = useState('');
  const [connecting, setConnecting] = useState(false);
  // When there's already ≥1 instance the form is collapsed behind an
  // "Add another instance" button; opening it must ADD (not replace) the
  // existing instances. `showForm` toggles only the form's visibility.
  const [showForm, setShowForm] = useState(false);
  const connectMutation = useConnectGitlab();
  // Per-instance disconnect (passes { integrationId } so it targets ONE instance).
  const disconnectMutation = useDisconnectIntegration('gitlab');
  const [removingId, setRemovingId] = useState('');

  // The account's gitlab integrations (multi-instance). NO tokens — only the
  // public-safe descriptor { integrationId, instanceUrl, username, status,
  // connectedAt }. Drives the connected-instances list + the link picker's
  // instance dropdown. Served by the Webhooks RestApi (GET /gitlab/integrations).
  const integrationsQuery = useGitlabIntegrations();
  const integrations = useMemo(
    () => integrationsQuery.data?.integrations || [],
    [integrationsQuery.data],
  );
  const hasAny = integrations.length > 0;

  useEffect(() => {
    if (isOpen) {
      setInstanceUrl('');
      setAccessToken('');
      setSigningToken('');
      setError('');
      setConnecting(false);
      setShowForm(false);
    }
  }, [isOpen]);

  const handleConnect = () => {
    if (!instanceUrl.trim() || !accessToken.trim()) {
      setError('GitLab URL and Personal Access Token are required.');
      return;
    }
    setConnecting(true);
    setError('');
    connectMutation.mutate(
      {
        instanceUrl: instanceUrl.trim(),
        accessToken: accessToken.trim(),
        signingToken: signingToken.trim(), // optional; hook drops it if blank
      },
      {
        onSuccess: () => {
          setConnecting(false);
          onConnected?.();
          // ADD semantics: a new instanceUrl created a NEW row. Clear the form
          // + collapse it so the refetched list shows the new instance; the
          // existing instances are untouched. The hook invalidates the
          // gitlab-integrations query so the list re-fetches automatically.
          setInstanceUrl('');
          setAccessToken('');
          setSigningToken('');
          setShowForm(false);
        },
        onError: (err) => {
          setConnecting(false);
          setError(err?.message || 'Failed to connect GitLab. Please check your URL and token.');
        },
      },
    );
  };

  // Show the connect form when there are no instances yet (first-run), or when
  // the user explicitly clicked "Add another instance".
  const formVisible = !hasAny || showForm;

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={() => onClose?.()}
      className="gitlab-connect-modal"
    >
      {/* Stop click propagation at the modal-content level so a click inside
          doesn't bubble up to a parent (marketplace) modal backdrop. */}
      <div
        className="gitlab-modal-content"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <h2>Connect GitLab</h2>

        {/* ── Connected instances list (multi-instance) ───────────────────── */}
        {hasAny && (
          <div className="gitlab-setup-section" style={{ marginTop: 0, paddingTop: 0, borderTop: 'none' }}>
            <h3 className="gitlab-section-title">Connected GitLab instances</h3>
            <p className="gitlab-modal-description">
              {integrations.length === 1
                ? 'One GitLab instance is connected to this account.'
                : `${integrations.length} GitLab instances are connected to this account.`}
            </p>
            <div className="gitlab-links-list">
              {integrations.map((it) => (
                <div key={it.integrationId} className="gitlab-link-row">
                  <span className="gitlab-link-label">
                    <strong>{it.instanceUrl || '(unknown instance)'}</strong>
                    {it.username ? <> · {it.username}</> : null}
                    {it.status ? <> · <code>{it.status}</code></> : null}
                  </span>
                  <IonButton
                    size="small"
                    fill="clear"
                    color="danger"
                    disabled={disconnectMutation.isLoading}
                    onClick={() => {
                      // eslint-disable-next-line no-alert
                      if (!window.confirm(`Disconnect ${it.instanceUrl || 'this GitLab instance'}? Links pinned to it revert to the default instance.`)) return;
                      setRemovingId(it.integrationId);
                      disconnectMutation.mutate(
                        { integrationId: it.integrationId },
                        { onSettled: () => setRemovingId('') }
                      );
                    }}
                  >
                    {disconnectMutation.isLoading && removingId === it.integrationId
                      ? <IonSpinner name="crescent" style={{ width: 16, height: 16 }} />
                      : 'Remove'}
                  </IonButton>
                </div>
              ))}
            </div>
            {!showForm && (
              <IonButton
                expand="block"
                fill="outline"
                style={{ marginTop: 12 }}
                onClick={() => { setShowForm(true); setError(''); }}
              >
                Add another instance
              </IonButton>
            )}
          </div>
        )}

        {/* ── Section 1: CONNECT form (first-run OR "Add another instance") ── */}
        {formVisible && (
          <div className={hasAny ? 'gitlab-setup-section' : undefined}>
            {hasAny && <h3 className="gitlab-section-title">Add a GitLab instance</h3>}
            <p className="gitlab-modal-description">
              Enter a GitLab instance URL and a Personal Access Token with <code>read_api</code> and <code>read_repository</code> scopes. Use <code>https://gitlab.com</code> for GitLab.com, or your self-hosted URL.
            </p>

            <div className="gitlab-form-group">
              <label className="gitlab-form-label">GitLab Instance URL</label>
              <input
                type="text"
                value={instanceUrl}
                placeholder="https://gitlab.company.com"
                onChange={(e) => { setInstanceUrl(e.target.value || ''); setError(''); }}
                className="gitlab-form-input"
                disabled={connecting}
              />
            </div>

            <div className="gitlab-form-group">
              <label className="gitlab-form-label">Personal Access Token</label>
              <input
                type="password"
                value={accessToken}
                placeholder="glpat-xxxxxxxxxxxxxxxxxxxx"
                onChange={(e) => { setAccessToken(e.target.value || ''); setError(''); }}
                className="gitlab-form-input"
                disabled={connecting}
              />
            </div>

            <div className="gitlab-form-group">
              <label className="gitlab-form-label">
                Webhook Signing Token <span className="gitlab-form-optional">(optional)</span>
              </label>
              <input
                type="password"
                value={signingToken}
                placeholder="whsec_xxxxxxxxxxxxxxxxxxxx"
                onChange={(e) => { setSigningToken(e.target.value || ''); setError(''); }}
                className="gitlab-form-input"
                disabled={connecting}
              />
              <p className="gitlab-form-hint">
                Paste the signing token GitLab shows when you add the webhook below. It lets Zibby verify each delivery is genuinely from your GitLab. Leave blank to use the shared secret token instead.
              </p>
            </div>

            {error && <p className="gitlab-modal-error">{error}</p>}

            <div className="gitlab-modal-buttons">
              <IonButton
                expand="block"
                onClick={handleConnect}
                disabled={connecting || !instanceUrl.trim() || !accessToken.trim()}
              >
                {connecting ? <IonSpinner name="crescent" /> : (hasAny ? 'Add instance' : 'Connect')}
              </IonButton>
              <IonButton
                expand="block"
                fill="clear"
                onClick={() => {
                  // When adding-another, Cancel collapses the form (keeps the
                  // modal open on the list). On first-run, Cancel closes.
                  if (hasAny) { setShowForm(false); setError(''); }
                  else onClose?.();
                }}
                disabled={connecting}
              >
                Cancel
              </IonButton>
            </div>
          </div>
        )}

        {/* ── WEBHOOK SETUP + REPO→AGENT PICKER (only once ≥1 instance) ────── */}
        {hasAny && (
          <>
            <WebhookSetup />
            <RepoAgentPicker integrations={integrations} />
            <div className="gitlab-modal-buttons">
              <IonButton expand="block" fill="clear" onClick={() => onClose?.()}>
                Done
              </IonButton>
            </div>
          </>
        )}
      </div>
    </IonModal>
  );
}

/**
 * Section 2 — show the webhook URL + secret the user must configure in GitLab,
 * plus the exact events to enable. Mirrors how the GitHub setup surfaces its
 * webhook config; here the user wires it by hand in their GitLab.
 */
function WebhookSetup() {
  const [copied, setCopied] = useState('');
  const url = webhookUrl();

  const copy = (text, which) => {
    try {
      navigator.clipboard?.writeText(text);
      setCopied(which);
      setTimeout(() => setCopied(''), 1500);
    } catch {
      /* clipboard unavailable — the value is still visible to copy manually */
    }
  };

  return (
    <div className="gitlab-setup-section">
      <h3 className="gitlab-section-title">1. Add a webhook in GitLab</h3>
      <p className="gitlab-modal-description">
        In your GitLab project (or group) go to <code>Settings → Webhooks → Add new webhook</code> and use these values:
      </p>

      <div className="gitlab-form-group">
        <label className="gitlab-form-label">Webhook URL</label>
        <div className="gitlab-copy-row">
          <input className="gitlab-form-input" value={url} readOnly onFocus={(e) => e.target.select()} />
          <button type="button" className="gitlab-copy-btn" onClick={() => copy(url, 'url')}>
            {copied === 'url' ? 'Copied' : 'Copy'}
          </button>
        </div>
      </div>

      <p className="gitlab-modal-description" style={{ marginBottom: 8 }}>
        <strong>Secret token:</strong> paste the Signing Token you entered when connecting (or set GitLab&apos;s
        secret token to your shared Zibby value). <br />
        <strong>Trigger:</strong> enable <code>Merge request events</code> and <code>Comments</code>.
      </p>
    </div>
  );
}

/**
 * Section 3 — repo/group → agent picker. The GitLab analog of the GitHub
 * repo→agent link. Creating a link POSTs to /gitlab/links, which writes the
 * exact row gitlab-webhook.js resolveLink reads to route an inbound MR.
 */
function RepoAgentPicker({ integrations = [] }) {
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [workflows, setWorkflows] = useState([]);
  const [agentSlug, setAgentSlug] = useState('');
  const [glRepos, setGlRepos] = useState([]);
  const [kind, setKind] = useState('project'); // 'project' | 'group' | 'path'
  const [identifier, setIdentifier] = useState('');
  // OPTIONAL: pin WHICH gitlab integration this link's reviews route to. Empty
  // = the account's first integration (back-compat). Only shown when the
  // account has more than one integration.
  const [integrationId, setIntegrationId] = useState('');
  const [pickerError, setPickerError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const linksQuery = useGitlabLinks(projectId);
  const createLink = useCreateGitlabLink(projectId);
  const deleteLink = useDeleteGitlabLink(projectId);

  // Load the user's Zibby projects once.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await api.get('/projects');
        if (alive) setProjects(Array.isArray(data) ? data : (data?.projects || []));
      } catch {
        /* non-fatal — the picker just shows no projects */
      }
    })();
    return () => { alive = false; };
  }, []);

  // Load the GitLab projects (for the project/path identifier dropdown).
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await api.get('/gitlab/repos');
        if (alive) setGlRepos(Array.isArray(data?.repos) ? data.repos : []);
      } catch {
        /* non-fatal */
      }
    })();
    return () => { alive = false; };
  }, []);

  // Load the selected project's deployed agents (workflows).
  useEffect(() => {
    let alive = true;
    setAgentSlug('');
    if (!projectId) { setWorkflows([]); return undefined; }
    (async () => {
      try {
        const data = await api.get(`/projects/${projectId}/workflows`);
        if (alive) setWorkflows(Array.isArray(data) ? data : []);
      } catch {
        if (alive) setWorkflows([]);
      }
    })();
    return () => { alive = false; };
  }, [projectId]);

  // Reset the identifier when switching kind (a project id is meaningless as a
  // group path and vice versa).
  useEffect(() => { setIdentifier(''); }, [kind]);

  // GitLab repos as { id, full_name } for the project/path dropdowns.
  const glOptions = useMemo(() => glRepos.map((r) => ({
    id: r.id,
    fullName: r.full_name,
  })), [glRepos]);

  const links = linksQuery.data?.links || [];

  // integrationId → instanceUrl, so a link row can show which instance its
  // reviews route to. Links with no integrationId use the account default.
  const integrationById = useMemo(() => {
    const m = {};
    for (const it of integrations) m[it.integrationId] = it;
    return m;
  }, [integrations]);

  const handleCreate = () => {
    setPickerError('');
    if (!projectId) { setPickerError('Pick a Zibby project.'); return; }
    if (!agentSlug) { setPickerError('Pick an agent to review merge requests.'); return; }
    if (!identifier) { setPickerError('Pick or enter the GitLab project / group.'); return; }
    setSubmitting(true);
    // Only send integrationId when the user explicitly pinned one. Absent =
    // the account's first integration (back-compat with single-instance links).
    const linkBody = { kind, identifier: identifier.trim(), projectId, agentSlug };
    if (integrationId) linkBody.integrationId = integrationId;
    createLink.mutate(
      linkBody,
      {
        onSuccess: () => { setSubmitting(false); setIdentifier(''); },
        onError: (err) => {
          setSubmitting(false);
          setPickerError(err?.message || 'Failed to create link.');
        },
      },
    );
  };

  const handleDelete = (link) => {
    deleteLink.mutate({ kind: link.glKind, identifier: link.glIdentifier });
  };

  return (
    <div className="gitlab-setup-section">
      <h3 className="gitlab-section-title">2. Link a GitLab project to a review agent</h3>
      <p className="gitlab-modal-description">
        Choose which deployed agent reviews merge requests for a GitLab project or group.
      </p>

      <div className="gitlab-form-group">
        <label className="gitlab-form-label">Zibby project</label>
        <select className="gitlab-form-input" value={projectId} onChange={(e) => setProjectId(e.target.value)}>
          <option value="">Select a project…</option>
          {projects.map((p) => (
            <option key={p.projectId || p.id} value={p.projectId || p.id}>
              {p.name || p.projectName || p.projectId || p.id}
            </option>
          ))}
        </select>
      </div>

      <div className="gitlab-form-group">
        <label className="gitlab-form-label">Review agent</label>
        <select
          className="gitlab-form-input"
          value={agentSlug}
          onChange={(e) => setAgentSlug(e.target.value)}
          disabled={!projectId}
        >
          <option value="">{projectId ? 'Select an agent…' : 'Pick a project first'}</option>
          {workflows.map((w) => (
            <option key={w.workflowType} value={w.workflowType}>
              {w.displayName || w.workflowType}
            </option>
          ))}
        </select>
      </div>

      {/* Instance picker — only when the account has >1 gitlab integration.
          Sets integrationId on the link so its reviews route to the right
          instance. Default ("Account default") sends no integrationId =
          back-compat first-integration behavior. */}
      {integrations.length > 1 && (
        <div className="gitlab-form-group">
          <label className="gitlab-form-label">GitLab instance</label>
          <select
            className="gitlab-form-input"
            value={integrationId}
            onChange={(e) => setIntegrationId(e.target.value)}
          >
            <option value="">Account default (first instance)</option>
            {integrations.map((it) => (
              <option key={it.integrationId} value={it.integrationId}>
                {it.instanceUrl || it.integrationId}{it.username ? ` · ${it.username}` : ''}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="gitlab-form-group">
        <label className="gitlab-form-label">Link type</label>
        <select className="gitlab-form-input" value={kind} onChange={(e) => setKind(e.target.value)}>
          <option value="project">A single GitLab project</option>
          <option value="group">A whole GitLab group (all projects under it)</option>
          <option value="path">A GitLab project by path</option>
        </select>
      </div>

      <div className="gitlab-form-group">
        <label className="gitlab-form-label">
          {kind === 'group' ? 'GitLab group path or id' : 'GitLab project'}
        </label>
        {kind === 'project' ? (
          <select className="gitlab-form-input" value={identifier} onChange={(e) => setIdentifier(e.target.value)}>
            <option value="">Select a GitLab project…</option>
            {glOptions.map((o) => (
              <option key={o.id} value={String(o.id)}>{o.fullName} (id {o.id})</option>
            ))}
          </select>
        ) : kind === 'path' ? (
          <select className="gitlab-form-input" value={identifier} onChange={(e) => setIdentifier(e.target.value)}>
            <option value="">Select a GitLab project…</option>
            {glOptions.map((o) => (
              <option key={o.id} value={o.fullName}>{o.fullName}</option>
            ))}
          </select>
        ) : (
          <input
            className="gitlab-form-input"
            value={identifier}
            placeholder="acme/team-a"
            onChange={(e) => setIdentifier(e.target.value || '')}
          />
        )}
      </div>

      {pickerError && <p className="gitlab-modal-error">{pickerError}</p>}

      <IonButton
        expand="block"
        onClick={handleCreate}
        disabled={submitting || !projectId || !agentSlug || !identifier}
      >
        {submitting ? <IonSpinner name="crescent" /> : 'Add link'}
      </IonButton>

      {/* Existing links for the selected project */}
      {projectId && links.length > 0 && (
        <div className="gitlab-links-list">
          {links.map((l) => (
            <div key={l.PK} className="gitlab-link-row">
              <span className="gitlab-link-label">
                <code>{l.glKind}</code> {l.glIdentifier} → <strong>{l.agentSlug}</strong>
                {integrations.length > 1 && (
                  <> · <code>{l.integrationId
                    ? (integrationById[l.integrationId]?.instanceUrl || l.integrationId)
                    : 'default instance'}</code></>
                )}
              </span>
              <button type="button" className="gitlab-link-remove" onClick={() => handleDelete(l)}>
                Remove
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
